import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/Button';
import { useToast } from '../components/Toast';
import { AlertCircle, CheckCircle, FileText, Clock, DollarSign, Users } from 'lucide-react';
import { ticketZService } from '../services/firebaseService';

export const TicketZ = () => {
  const toast = useToast();
  const [dailyStatus, setDailyStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [pendingDays, setPendingDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closingAll, setClosingAll] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    loadDailyStatus();
    loadTickets();
    loadPendingDays();
  }, []);

  const loadDailyStatus = async () => {
    try {
      const data = await ticketZService.getDailyStatus(today);
      setDailyStatus(data);
    } catch (error) {
      console.error('Error loading daily status:', error);
    }
  };

  const loadTickets = async () => {
    try {
      const result = await ticketZService.list(30);
      setTickets(result.tickets || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    }
  };

  const loadPendingDays = async () => {
    try {
      const result = await ticketZService.getPendingDays();
      setPendingDays(result.pending_days || []);
    } catch (error) {
      console.error('Error loading pending days:', error);
    }
  };

  const handleCloseAllPending = async () => {
    const closableDays = pendingDays.filter(d => d.can_close);
    if (closableDays.length === 0) {
      toast.error('Impossible', 'Aucun jour ne peut être clôturé (commandes en attente)');
      return;
    }

    if (!window.confirm(`⚠️ Clôturer ${closableDays.length} jour(s) en attente ? Cette action est irréversible.`)) {
      return;
    }

    setClosingAll(true);
    try {
      let closedCount = 0;
      for (const day of closableDays) {
        await ticketZService.closeDay(day.date);
        closedCount++;
      }
      toast.success('Clôture réussie', `${closedCount} jour(s) clôturé(s)`);
      loadDailyStatus();
      loadTickets();
      loadPendingDays();
    } catch (error) {
      toast.error('Erreur clôture', error.message || 'Erreur lors de la clôture');
    } finally {
      setClosingAll(false);
    }
  };

  const handleCloseDay = async () => {
    if (!window.confirm('⚠️ Confirmer la clôture de journée ? Cette action est irréversible.')) {
      return;
    }

    setLoading(true);
    try {
      await ticketZService.closeDay(today);
      toast.success('Journée clôturée', 'Le Ticket Z a été généré avec succès');
      loadDailyStatus();
      loadTickets();
    } catch (error) {
      toast.error('Erreur clôture', error.message || 'Erreur lors de la clôture');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header title="📊 Ticket Z" subtitle="Clôture de journée et rapport comptable" />

      <div className="p-6 max-w-7xl mx-auto">

      {/* Status de la journée actuelle */}
      {dailyStatus && (
        <div className="mb-8">
          <div className={`p-6 rounded-lg border-2 ${
            dailyStatus.is_closed
              ? 'bg-green-50 border-green-500'
              : 'bg-blue-50 border-blue-500'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2 flex items-center">
                  {dailyStatus.is_closed ? (
                    <><CheckCircle className="w-6 h-6 text-green-600 mr-2" /> Journée clôturée</>
                  ) : (
                    <><Clock className="w-6 h-6 text-blue-600 mr-2" /> Journée en cours</>
                  )}
                </h2>
                <p className="text-gray-700 mb-3">Date : {dailyStatus.date}</p>

                {!dailyStatus.is_closed && (
                  <div className="text-sm text-gray-600">
                    Le Ticket Z sera généré automatiquement demain à 4h du matin.
                  </div>
                )}
              </div>
            </div>

            {dailyStatus.is_closed && dailyStatus.ticket_z && (
              <div className="mt-4 pt-4 border-t border-green-300">
                <Button
                  onClick={() => setSelectedTicket(dailyStatus.ticket_z)}
                  className="bg-white text-green-700 border-2 border-green-500 hover:bg-green-100"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Voir le Ticket Z
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Jours en attente de clôture */}
      {pendingDays.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">⚠️ Jours en attente de clôture ({pendingDays.length})</h2>
            <Button
              onClick={handleCloseAllPending}
              disabled={closingAll || !pendingDays.some(d => d.can_close)}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-6 py-2"
            >
              {closingAll ? 'Clôture en cours...' : '🔒 Clôturer tous les jours'}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {pendingDays.map(day => (
              <div
                key={day.date}
                className={`p-4 rounded-lg border-2 ${
                  day.can_close
                    ? 'bg-green-50 border-green-400'
                    : 'bg-red-50 border-red-400'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-lg">{day.date}</span>
                  {day.can_close ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Commandes:</span>
                    <span className="font-bold">{day.total_orders}</span>
                  </div>
                  {day.pending_orders > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>❌ Non terminées:</span>
                      <span className="font-bold">{day.pending_orders}</span>
                    </div>
                  )}
                  {day.unpaid_orders > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span>❌ Non payées:</span>
                      <span className="font-bold">{day.unpaid_orders}</span>
                    </div>
                  )}
                </div>
                {!day.can_close && (
                  <p className="text-xs text-red-600 mt-2 font-medium">
                    ⚠️ {day.pending_orders > 0 ? 'Terminer' : ''}{day.pending_orders > 0 && day.unpaid_orders > 0 ? ' et ' : ''}{day.unpaid_orders > 0 ? 'Encaisser' : ''} les commandes avant clôture
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historique des Tickets Z */}
      <div>
        <h2 className="text-2xl font-bold mb-4">📜 Historique des clôtures</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tickets.map(ticket => (
            <div
              key={ticket.id}
              onClick={() => setSelectedTicket(ticket)}
              className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg cursor-pointer transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-lg">{ticket.date}</span>
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Ventes:</span>
                  <span className="font-bold text-green-600">{ticket.total_sales.toFixed(2)}€</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Commandes:</span>
                  <span className="font-bold">{ticket.total_orders}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Couverts:</span>
                  <span className="font-bold">{ticket.covers_count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal détail Ticket Z */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">📊 Ticket Z - {selectedTicket.date}</h2>
              <button
                onClick={() => setSelectedTicket(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              {/* Résumé */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-sm text-gray-600">Ventes totales</span>
                  </div>
                  <p className="text-3xl font-bold text-green-600">{selectedTicket.total_sales.toFixed(2)}€</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Users className="w-5 h-5 text-blue-600 mr-2" />
                    <span className="text-sm text-gray-600">Couverts</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-600">{selectedTicket.covers_count}</p>
                </div>
              </div>

              {/* Commandes */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-bold mb-3">📦 Commandes</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-2xl font-bold">{selectedTicket.total_orders}</p>
                    <p className="text-xs text-gray-600">Total</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{selectedTicket.completed_orders}</p>
                    <p className="text-xs text-gray-600">Terminées</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{selectedTicket.cancelled_orders}</p>
                    <p className="text-xs text-gray-600">Annulées</p>
                  </div>
                </div>
              </div>

              {/* Répartition paiements */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-bold mb-3">💳 Répartition des paiements</h3>
                <div className="space-y-2">
                  {selectedTicket.payment_breakdown.espece > 0 && (
                    <div className="flex justify-between p-2 bg-green-100 rounded">
                      <span className="font-medium">💵 Espèce</span>
                      <span className="font-bold">{selectedTicket.payment_breakdown.espece.toFixed(2)}€</span>
                    </div>
                  )}
                  {selectedTicket.payment_breakdown.cb > 0 && (
                    <div className="flex justify-between p-2 bg-blue-100 rounded">
                      <span className="font-medium">💳 CB</span>
                      <span className="font-bold">{selectedTicket.payment_breakdown.cb.toFixed(2)}€</span>
                    </div>
                  )}
                  {selectedTicket.payment_breakdown.cheque > 0 && (
                    <div className="flex justify-between p-2 bg-purple-100 rounded">
                      <span className="font-medium">📝 Chèque</span>
                      <span className="font-bold">{selectedTicket.payment_breakdown.cheque.toFixed(2)}€</span>
                    </div>
                  )}
                  {selectedTicket.payment_breakdown.ticket_restaurant > 0 && (
                    <div className="flex justify-between p-2 bg-orange-100 rounded">
                      <span className="font-medium">🎟️ Ticket restaurant</span>
                      <span className="font-bold">{selectedTicket.payment_breakdown.ticket_restaurant.toFixed(2)}€</span>
                    </div>
                  )}
                  {selectedTicket.payment_breakdown.online > 0 && (
                    <div className="flex justify-between p-2 bg-indigo-100 rounded">
                      <span className="font-medium">🌐 Paiement en ligne</span>
                      <span className="font-bold">{selectedTicket.payment_breakdown.online.toFixed(2)}€</span>
                    </div>
                  )}
                </div>
              </div>

              {/* TVA — le taux appliqué est affiché : un montant de TVA sans son
                  taux n'est pas vérifiable par un comptable. Les tickets clôturés
                  avant le 2026-08-23 n'ont pas de `tva_rate` enregistré. */}
              <div className="p-4 bg-yellow-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-bold">
                    💰 TVA collectée
                    {typeof selectedTicket.tva_rate === 'number' && (
                      <span className="ml-2 font-normal text-sm text-gray-600">
                        (taux {selectedTicket.tva_rate}%)
                      </span>
                    )}
                  </span>
                  <span className="text-2xl font-bold text-yellow-700">{(selectedTicket.tva_collected || 0).toFixed(2)}€</span>
                </div>
                {selectedTicket.tva_rate_source === 'default' && (
                  <p className="mt-2 text-xs text-yellow-800">
                    ⚠️ Aucun taux de TVA n'est configuré : le taux par défaut du
                    logiciel a été appliqué. Réglez-le dans Réglages, après
                    validation par votre comptable.
                  </p>
                )}
              </div>

              {/* Métadonnées */}
              <div className="pt-4 border-t text-xs text-gray-500">
                <p>Clôturé par: {selectedTicket.closed_by}</p>
                <p>Le: {selectedTicket.closed_at?._seconds
                  ? new Date(selectedTicket.closed_at._seconds * 1000).toLocaleString('fr-FR')
                  : selectedTicket.closed_at
                    ? new Date(selectedTicket.closed_at).toLocaleString('fr-FR')
                    : 'Date inconnue'
                }</p>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};
